import React from "react";

const DailySection = ({
  sectionNumber,
  formData,
  handleChange,
  handleCheckboxChange,
  handleRadioClick,
  dtDisplay,
}) => {
  const prefix = `d${sectionNumber}_`;

  // 복사-붙여넣기 자동 포맷팅 핸들러
  const handlePaste = (e, fieldName) => {
    e.preventDefault(); // 기본 붙여넣기 동작 방지

    const pastedData = e.clipboardData.getData("text");
    const numbers = pastedData.replace(/\D/g, ""); // 숫자만 추출

    if (numbers.length <= 12) {
      // 자동 포맷팅 적용
      let formattedValue = numbers;

      if (numbers.length >= 4) {
        formattedValue = numbers.slice(0, 4) + "-" + numbers.slice(4);
      }
      if (numbers.length >= 6) {
        formattedValue =
          formattedValue.slice(0, 7) + "-" + formattedValue.slice(7);
      }
      if (numbers.length >= 8) {
        formattedValue =
          formattedValue.slice(0, 10) + " " + formattedValue.slice(10);
      }
      if (numbers.length >= 10) {
        formattedValue =
          formattedValue.slice(0, 13) + ":" + formattedValue.slice(13);
      }

      // 포맷된 값으로 필드 업데이트
      handleChange({
        target: { name: fieldName, value: formattedValue },
      });
    }
  };

  // GCS 최악값 총점 자동 계산 로직
  React.useEffect(() => {
    const e_score = parseInt(
      formData[`${prefix}gcs_worst_e`]?.split(":")[0] || 0
    );
    const v_score = parseInt(
      formData[`${prefix}gcs_worst_v`]?.split(":")[0] || 0
    );
    const m_score = parseInt(
      formData[`${prefix}gcs_worst_m`]?.split(":")[0] || 0
    );

    let result = 0;
    if (e_score === 0 || m_score === 0) {
      result = 0;
    } else {
      const sumScore = e_score + m_score;
      let additionalValue = 0;

      if (v_score === 0) {
        if (sumScore <= 6) additionalValue = 1;
        else if (sumScore === 7) additionalValue = 2;
        else if (sumScore >= 8 && sumScore < 10) additionalValue = 4;
        else if (sumScore === 10) additionalValue = 5;
      }
      result = sumScore + v_score + additionalValue;
    }

    handleChange({
      target: {
        name: `${prefix}gcs_worst_total`,
        value: result.toString(),
      },
    });
  }, [
    formData[`${prefix}gcs_worst_e`],
    formData[`${prefix}gcs_worst_v`],
    formData[`${prefix}gcs_worst_m`],
  ]);

  // GCS 최선값 총점 자동 계산 로직
  React.useEffect(() => {
    const e_score = parseInt(
      formData[`${prefix}gcs_best_e`]?.split(":")[0] || 0
    );
    const v_score = parseInt(
      formData[`${prefix}gcs_best_v`]?.split(":")[0] || 0
    );
    const m_score = parseInt(
      formData[`${prefix}gcs_best_m`]?.split(":")[0] || 0
    );

    let result = 0;
    if (e_score === 0 || m_score === 0) {
      result = 0;
    } else {
      const sumScore = e_score + m_score;
      let additionalValue = 0;

      if (v_score === 0) {
        if (sumScore <= 6) additionalValue = 1;
        else if (sumScore === 7) additionalValue = 2;
        else if (sumScore >= 8 && sumScore < 10) additionalValue = 4;
        else if (sumScore === 10) additionalValue = 5;
      }
      result = sumScore + v_score + additionalValue;
    }

    handleChange({
      target: {
        name: `${prefix}gcs_best_total`,
        value: result.toString(),
      },
    });
  }, [
    formData[`${prefix}gcs_best_e`],
    formData[`${prefix}gcs_best_v`],
    formData[`${prefix}gcs_best_m`],
  ]);

  // TIL 총점 자동 계산 로직
  React.useEffect(() => {
    let total = 0;

    // 1. Position Management (최대값 적용)
    const position_score = Math.max(
      formData[`${prefix}til_position`] === "1 : Yes" ? 1 : 0,
      formData[`${prefix}til_position_flat`] === "1 : Yes" ? 1 : 0
    );
    total += position_score;

    // 2. Sedation Management (최대값 적용)
    const sedation_score = Math.max(
      formData[`${prefix}til_sedation`] === "1 : Yes" ? 1 : 0,
      formData[`${prefix}til_sedation_higher`] === "1 : Yes" ? 2 : 0,
      formData[`${prefix}til_sedation_suppression`] === "1 : Yes" ? 5 : 0
    );
    total += sedation_score;

    // 3. Neuromuscular Blockade
    if (formData[`${prefix}til_sedation_nmb`] === "1 : Yes") total += 3;

    // 4. CSF Drainage
    if (formData[`${prefix}til_csfdrain`] === "1 : Yes") {
      const volume = parseFloat(formData[`${prefix}til_csfdrainvol`]) || 0;
      total += volume >= 120 ? 3 : 2;
    }

    // 5. Fluid/BP Management (합산)
    if (formData[`${prefix}til_fluidloading`] === "1 : Yes") total += 1;
    if (formData[`${prefix}til_vasopressor`] === "1 : Yes") total += 1;

    // 6. Ventilation Management (최대값 적용)
    const ventilation_score = Math.max(
      formData[`${prefix}til_hyperventilation`] === "1 : Yes" ? 1 : 0,
      formData[`${prefix}til_hyperventilation_mod`] === "1 : Yes" ? 2 : 0,
      formData[`${prefix}til_hyperventilation_intense`] === "1 : Yes" ? 4 : 0
    );
    total += ventilation_score;

    // 7. Osmolar Therapy (각각 최대값 적용)
    const mannitol_score = Math.max(
      formData[`${prefix}til_osm_mnt_low`] === "1 : Yes" ? 2 : 0,
      formData[`${prefix}til_osm_mnt_high`] === "1 : Yes" ? 3 : 0
    );
    const hts_score = Math.max(
      formData[`${prefix}til_osm_hts_low`] === "1 : Yes" ? 2 : 0,
      formData[`${prefix}til_osm_hts_high`] === "1 : Yes" ? 3 : 0
    );
    total += mannitol_score + hts_score;

    // 8. Temperature Management (최대값 적용)
    const temp_score = Math.max(
      formData[`${prefix}til_fever`] === "1 : Yes" ? 1 : 0,
      formData[`${prefix}til_ttm_mild`] === "1 : Yes" ? 2 : 0,
      formData[`${prefix}til_ttm_moderate`] === "1 : Yes" ? 5 : 0
    );
    total += temp_score;

    // 9. Surgery Management (합산)
    if (formData[`${prefix}til_icp_surgery`] === "1 : Yes") total += 4;
    if (formData[`${prefix}til_icp_decomcrani`] === "1 : Yes") total += 5;

    handleChange({
      target: {
        name: `${prefix}til_summary`,
        value: total.toString(),
      },
    });
  }, [
    formData[`${prefix}til_position`],
    formData[`${prefix}til_position_flat`],
    formData[`${prefix}til_sedation`],
    formData[`${prefix}til_sedation_higher`],
    formData[`${prefix}til_sedation_suppression`],
    formData[`${prefix}til_sedation_nmb`],
    formData[`${prefix}til_csfdrain`],
    formData[`${prefix}til_csfdrainvol`],
    formData[`${prefix}til_fluidloading`],
    formData[`${prefix}til_vasopressor`],
    formData[`${prefix}til_hyperventilation`],
    formData[`${prefix}til_hyperventilation_mod`],
    formData[`${prefix}til_hyperventilation_intense`],
    formData[`${prefix}til_osm_mnt_low`],
    formData[`${prefix}til_osm_mnt_high`],
    formData[`${prefix}til_osm_hts_low`],
    formData[`${prefix}til_osm_hts_high`],
    formData[`${prefix}til_fever`],
    formData[`${prefix}til_ttm_mild`],
    formData[`${prefix}til_ttm_moderate`],
    formData[`${prefix}til_icp_surgery`],
    formData[`${prefix}til_icp_decomcrani`],
  ]);

  return (
    <div className="basic-info-fieldset">
      <h3 className="section-subtitle">Daily {sectionNumber}</h3>

      {/* Daily 작성시 환자의 위치 */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Daily 작성시 환자의 위치 ({prefix}location)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {["ICU", "Sub-ICU/Stroke Unit", "Ward"].map((opt) => (
                <label key={opt} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}location`}
                    value={opt}
                    checked={formData[`${prefix}location`] === opt}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="block-2column">
          <label className="item_title_2col">
            Daily {sectionNumber} Status
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {["Not started", "In process", "Complete"].map((status) => (
                <label key={status} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}status`}
                    value={status}
                    checked={formData[`${prefix}status`] === status}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{status}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* GCS Assessment Time and Conditions */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            time of worst GCS ({prefix}gcs_worst_dt)
          </label>
          <div className="input_2col">
            <input
              type="text"
              name={`${prefix}gcs_worst_dt`}
              placeholder="YYYY-MM-DD HH:MM"
              value={dtDisplay(formData[`${prefix}gcs_worst_dt`])}
              onChange={(e) => {
                const input = e.target.value;
                const currentValue =
                  dtDisplay(formData[`${prefix}gcs_worst_dt`]) || "";

                // 현재 입력된 값에서 이전 값보다 짧아졌다면 백스페이스로 간주
                if (input.length < currentValue.length) {
                  handleChange(e);
                  return;
                }

                // 숫자만 추출
                const numbers = input.replace(/\D/g, "");

                if (numbers.length <= 12) {
                  // 최대 12자리 (YYYYMMDDHHmm)
                  let formattedValue = numbers;

                  // 날짜 시간 포맷팅
                  if (numbers.length >= 4) {
                    formattedValue =
                      numbers.slice(0, 4) + "-" + numbers.slice(4);
                  }
                  if (numbers.length >= 6) {
                    formattedValue =
                      formattedValue.slice(0, 7) +
                      "-" +
                      formattedValue.slice(7);
                  }
                  if (numbers.length >= 8) {
                    formattedValue =
                      formattedValue.slice(0, 10) +
                      " " +
                      formattedValue.slice(10);
                  }
                  if (numbers.length >= 10) {
                    formattedValue =
                      formattedValue.slice(0, 13) +
                      ":" +
                      formattedValue.slice(13);
                  }

                  // handleChange 함수 호출
                  handleChange({
                    target: {
                      name: `${prefix}gcs_worst_dt`,
                      value: formattedValue,
                    },
                  });
                }
              }}
              onPaste={(e) => handlePaste(e, `${prefix}gcs_worst_dt`)}
            />
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Worst GCS Assessment conditions ({prefix}gcs_worst_asscond)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                "No sedation or paralysis",
                "Sedated",
                "Paralyzed",
                "Temporary stop of sedation/paralysis",
                "Reversal of sedation/paralysis",
                "Active reversal (pharmacologic) of sedation/paralysis",
                "Other",
              ].map((opt) => (
                <label key={opt} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}gcs_worst_asscond`}
                    value={opt}
                    checked={formData[`${prefix}gcs_worst_asscond`] === opt}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text" style={{ fontSize: "12px" }}>
                    {opt}
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* GCS Worst Assessment */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Worst GCS_E ({prefix}gcs_worst_e)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "1 : None", value: "1 : None" },
                { label: "2 : To pain", value: "2 : To pain" },
                { label: "3 : To speech", value: "3 : To speech" },
                { label: "4 : Spontaneously", value: "4 : Spontaneously" },
                {
                  label: "Untestable (swollen)",
                  value: "0 : Untestable (swollen)",
                },
                {
                  label: "Untestable (other)",
                  value: "0 : Untestable (other)",
                },
                { label: "Unknown", value: "0 : Unknown" },
              ].map((option) => (
                <label key={option.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}gcs_worst_e`}
                    value={option.value}
                    checked={formData[`${prefix}gcs_worst_e`] === option.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{option.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Worst GCS_V ({prefix}gcs_worst_v)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "1 : None", value: "1 : None" },
                {
                  label: "2 : Incomprehensible sound",
                  value: "2 : Incomprehensible sound",
                },
                {
                  label: "3 : Inappropriate words",
                  value: "3 : Inappropriate words",
                },
                { label: "4 : Confused", value: "4 : Confused" },
                { label: "5 : Oriented", value: "5 : Oriented" },
                {
                  label: "Untestable (tracheotomy/endotracheal tube)",
                  value: "0 : Untestable (tracheotomy/endotracheal tube)",
                },
                {
                  label: "Untestable (Other)",
                  value: "0 : Untestable (Other)",
                },
                { label: "Unknown", value: "0 : Unknown" },
              ].map((option) => (
                <label key={option.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}gcs_worst_v`}
                    value={option.value}
                    checked={formData[`${prefix}gcs_worst_v`] === option.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text" style={{ fontSize: "13px" }}>
                    {option.label}
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Worst GCS_m ({prefix}gcs_worst_m)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "1 : None", value: "1 : None" },
                {
                  label: "2 : Abnormal extension",
                  value: "2 : Abnormal extension",
                },
                {
                  label: "3 : Abnormal flexion",
                  value: "3 : Abnormal flexion",
                },
                {
                  label: "4 : Normal flexion/withdrawal",
                  value: "4 : Normal flexion/withdrawal",
                },
                {
                  label: "5 : Localizes to pain",
                  value: "5 : Localizes to pain",
                },
                {
                  label: "6 : Obeys command",
                  value: "6 : Obeys command",
                },
                {
                  label: "Untestable (Deep sedation/paralyzed)",
                  value: "0 : Untestable (Deep sedation/paralyzed)",
                },
                {
                  label: "Untestable (other)",
                  value: "0 : Untestable (Other)",
                },
                { label: "Unknown", value: "0 : Unknown" },
              ].map((option) => (
                <label key={option.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}gcs_worst_m`}
                    value={option.value}
                    checked={formData[`${prefix}gcs_worst_m`] === option.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text" style={{ fontSize: "13px" }}>
                    {option.label}
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Worst GCS_total ({prefix}gcs_worst_total)
            <span className="tooltip-container">
              <span className="tooltip-icon">?</span>
              <span className="tooltip-text">
                【GCS Total Score 자동계산 공식】
                <br />
                <br />
                ★ 필수 입력 필드 (3개):
                <br />• GCS_E (Eye 반응)
                <br />• GCS_V (Verbal 반응)
                <br />• GCS_M (Motor 반응)
                <br />
                <br />
                ★ 계산 로직:
                <br />
                1) E=0 또는 M=0인 경우
                <br />
                &nbsp;&nbsp;&nbsp;→ 총점 0점 (Untestable/Unknown)
                <br />
                <br />
                2) V=0인 경우 (기관삽관 등):
                <br />
                &nbsp;&nbsp;&nbsp;→ (E+M) ≤6: +1점
                <br />
                &nbsp;&nbsp;&nbsp;→ (E+M) =7: +2점
                <br />
                &nbsp;&nbsp;&nbsp;→ (E+M) 8-9: +4점
                <br />
                &nbsp;&nbsp;&nbsp;→ (E+M) =10: +5점
                <br />
                <br />
                3) 그 외의 경우:
                <br />
                &nbsp;&nbsp;&nbsp;→ E + V + M 단순 합산
                <br />
                <br />★ 점수 범위: 0~15점
              </span>
            </span>
          </label>
          <div className="input_2col">
            <input
              type="text"
              name={`${prefix}gcs_worst_total`}
              value={formData[`${prefix}gcs_worst_total`] || ""}
              readOnly
              style={{
                backgroundColor: "#f5f5f5",
                cursor: "not-allowed",
                fontWeight: "bold",
                color: "#007bff",
              }}
            />
          </div>
        </div>
      </div>

      {/* Pupil Symmetry */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Worst pupil_symmetry ({prefix}pupil_worst_sym)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {["Equal", "Unequal R>L", "Unequal L>R"].map((opt) => (
                <label key={opt} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}pupil_worst_sym`}
                    value={opt}
                    checked={formData[`${prefix}pupil_worst_sym`] === opt}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="block-2column" />
      </div>

      {/* Right Pupil Assessment */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Worst pupil_reflex_right ({prefix}pupil_worst_lr_rt)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "+ (Brisk)", value: "(Brisk) +" },
                { label: "+ (Sluggish)", value: "(Sluggish) +" },
                { label: "- (Negative)", value: "(Negative) -" },
              ].map((option) => (
                <label key={option.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}pupil_worst_lr_rt`}
                    value={option.value}
                    checked={
                      formData[`${prefix}pupil_worst_lr_rt`] === option.value
                    }
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{option.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Worst pupil_size_right ({prefix}pupil_worst_size_rt)
          </label>
          <div className="input_2col">
            <input
              type="text"
              name={`${prefix}pupil_worst_size_rt`}
              value={formData[`${prefix}pupil_worst_size_rt`] || ""}
              onChange={handleChange}
            />
            <span className="unit-text">mm</span>
          </div>
        </div>
      </div>

      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Worst pupil_reflex_unknown_right ({prefix}pupil_worst_unknown_rt)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {["Untestable", "Unknown"].map((opt) => (
                <label key={opt} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}pupil_worst_unknown_rt`}
                    value={opt}
                    checked={
                      formData[`${prefix}pupil_worst_unknown_rt`] === opt
                    }
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="block-2column" />
      </div>

      {/* Left Pupil Assessment */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Worst pupil_reflex_left ({prefix}pupil_worst_lr_lt)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "+ (Brisk)", value: "(Brisk) +" },
                { label: "+ (Sluggish)", value: "(Sluggish) +" },
                { label: "- (Negative)", value: "(Negative) -" },
              ].map((option) => (
                <label key={option.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}pupil_worst_lr_lt`}
                    value={option.value}
                    checked={
                      formData[`${prefix}pupil_worst_lr_lt`] === option.value
                    }
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{option.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Worst pupil_size_left ({prefix}pupil_worst_size_lt)
          </label>
          <div className="input_2col">
            <input
              type="text"
              name={`${prefix}pupil_worst_size_lt`}
              value={formData[`${prefix}pupil_worst_size_lt`] || ""}
              onChange={handleChange}
            />
            <span className="unit-text">mm</span>
          </div>
        </div>
      </div>

      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Worst pupil_reflex_unknown_left ({prefix}pupil_worst_unknown_lt)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {["Untestable", "Unknown"].map((opt) => (
                <label key={opt} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}pupil_worst_unknown_lt`}
                    value={opt}
                    checked={
                      formData[`${prefix}pupil_worst_unknown_lt`] === opt
                    }
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="block-2column" />
      </div>

      {/* Best GCS Section */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Worst GCS same as best GCS? ({prefix}gcs_best)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {["No", "Yes"].map((opt) => (
                <label key={opt} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}gcs_best`}
                    value={opt}
                    checked={formData[`${prefix}gcs_best`] === opt}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            time of best GCS ({prefix}gcs_best_dt)
          </label>
          <div className="input_2col">
            <input
              type="text"
              name={`${prefix}gcs_best_dt`}
              placeholder="YYYY-MM-DD HH:MM"
              value={dtDisplay(formData[`${prefix}gcs_best_dt`])}
              onChange={(e) => {
                const input = e.target.value;
                const currentValue =
                  dtDisplay(formData[`${prefix}gcs_best_dt`]) || "";

                // 현재 입력된 값에서 이전 값보다 짧아졌다면 백스페이스로 간주
                if (input.length < currentValue.length) {
                  handleChange(e);
                  return;
                }

                // 숫자만 추출
                const numbers = input.replace(/\D/g, "");

                if (numbers.length <= 12) {
                  // 최대 12자리 (YYYYMMDDHHmm)
                  let formattedValue = numbers;

                  // 날짜 시간 포맷팅
                  if (numbers.length >= 4) {
                    formattedValue =
                      numbers.slice(0, 4) + "-" + numbers.slice(4);
                  }
                  if (numbers.length >= 6) {
                    formattedValue =
                      formattedValue.slice(0, 7) +
                      "-" +
                      formattedValue.slice(7);
                  }
                  if (numbers.length >= 8) {
                    formattedValue =
                      formattedValue.slice(0, 10) +
                      " " +
                      formattedValue.slice(10);
                  }
                  if (numbers.length >= 10) {
                    formattedValue =
                      formattedValue.slice(0, 13) +
                      ":" +
                      formattedValue.slice(13);
                  }

                  // handleChange 함수 호출
                  handleChange({
                    target: {
                      name: `${prefix}gcs_best_dt`,
                      value: formattedValue,
                    },
                  });
                }
              }}
              onPaste={(e) => handlePaste(e, `${prefix}gcs_best_dt`)}
            />
          </div>
        </div>
      </div>

      {/* Best GCS Assessment Conditions */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Best GCS Assessment conditions ({prefix}gcs_best_asscond)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                "No sedation or paralysis",
                "Sedated",
                "Paralyzed",
                "Temporary stop of sedation/paralysis",
                "Reversal of sedation/paralysis",
                "Active reversal (pharmacologic) of sedation/paralysis",
                "Other",
              ].map((opt) => (
                <label key={opt} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}gcs_best_asscond`}
                    value={opt}
                    checked={formData[`${prefix}gcs_best_asscond`] === opt}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text" style={{ fontSize: "12px" }}>
                    {opt}
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="block-2column" />
      </div>

      {/* GCS Change and Cause */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            GCS Change over past 24 hours ({prefix}gcs_best_change24h)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                "No change",
                "Improving",
                "Episode of deterioration",
                "Sustained deterioration",
                "Fluctuating",
              ].map((opt) => (
                <label key={opt} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}gcs_best_change24h`}
                    value={opt}
                    checked={formData[`${prefix}gcs_best_change24h`] === opt}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Cause of change ({prefix}gcs_best_change24h_cause)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                "Mainly intracranial",
                "Mainly extracranial",
                "Both to an equal extent",
                "Unknown",
              ].map((opt) => (
                <label key={opt} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}gcs_best_change24h_cause`}
                    value={opt}
                    checked={
                      formData[`${prefix}gcs_best_change24h_cause`] === opt
                    }
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Best GCS Components */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Best GCS_e ({prefix}gcs_best_e)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "1 : None", value: "1 : None" },
                { label: "2 : To pain", value: "2 : To pain" },
                { label: "3 : To speech", value: "3 : To speech" },
                { label: "4 : Spontaneously", value: "4 : Spontaneously" },
                {
                  label: "Untestable (swollen)",
                  value: "0 : Untestable (swollen)",
                },
                {
                  label: "Untestable (other)",
                  value: "0 : Untestable (Other)",
                },
                { label: "Unknown", value: "0 : Unknown" },
              ].map((option) => (
                <label key={option.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}gcs_best_e`}
                    value={option.value}
                    checked={formData[`${prefix}gcs_best_e`] === option.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{option.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Best GCS_v ({prefix}gcs_best_v)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "1 : None", value: "1 : None" },
                {
                  label: "2 : Incomprehensible sound",
                  value: "2 : Incomprehensible sound",
                },
                {
                  label: "3 : Inappropriate words",
                  value: "3 : Inappropriate words",
                },
                { label: "4 : Confused", value: "4 : Confused" },
                { label: "5 : Oriented", value: "5 : Oriented" },
                {
                  label: "Untestable (tracheotomy/endotracheal tube)",
                  value: "0 : Untestable (tracheotomy/endotracheal tube)",
                },
                {
                  label: "Untestable (other)",
                  value: "0 : Untestable (Other)",
                },
                { label: "Unknown", value: "0 : Unknown" },
              ].map((option) => (
                <label key={option.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}gcs_best_v`}
                    value={option.value}
                    checked={formData[`${prefix}gcs_best_v`] === option.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text" style={{ fontSize: "13px" }}>
                    {option.label}
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Best GCS_m ({prefix}gcs_best_m)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "1 : None", value: "1 : None" },
                {
                  label: "2 : Abnormal extension",
                  value: "2 : Abnormal extension",
                },
                {
                  label: "3 : Abnormal flexion",
                  value: "3 : Abnormal flexion",
                },
                {
                  label: "4 : Normal flexion/withdrawal",
                  value: "4 : Normal flexion/withdrawal",
                },
                {
                  label: "5 : Localizes to pain",
                  value: "5 : Localizes to pain",
                },
                {
                  label: "6 : Obeys command",
                  value: "6 : Obeys command",
                },
                {
                  label: "Untestable (Deep sedation/paralyzed)",
                  value: "0 : Untestable (Deep sedation/paralyzed)",
                },
                {
                  label: "Untestable (other)",
                  value: "0 : Untestable (Other)",
                },
                { label: "Unknown", value: "0 : Unknown" },
              ].map((option) => (
                <label key={option.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}gcs_best_m`}
                    value={option.value}
                    checked={formData[`${prefix}gcs_best_m`] === option.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text" style={{ fontSize: "13px" }}>
                    {option.label}
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Best GCS_total ({prefix}gcs_best_total)
            <span className="tooltip-container">
              <span className="tooltip-icon">?</span>
              <span className="tooltip-text">
                【GCS Total Score 자동계산 공식】
                <br />
                <br />
                ★ 필수 입력 필드 (3개):
                <br />• GCS_E (Eye 반응)
                <br />• GCS_V (Verbal 반응)
                <br />• GCS_M (Motor 반응)
                <br />
                <br />
                ★ 계산 로직:
                <br />
                1) E=0 또는 M=0인 경우
                <br />
                &nbsp;&nbsp;&nbsp;→ 총점 0점 (Untestable/Unknown)
                <br />
                <br />
                2) V=0인 경우 (기관삽관 등):
                <br />
                &nbsp;&nbsp;&nbsp;→ (E+M) ≤6: +1점
                <br />
                &nbsp;&nbsp;&nbsp;→ (E+M) =7: +2점
                <br />
                &nbsp;&nbsp;&nbsp;→ (E+M) 8-9: +4점
                <br />
                &nbsp;&nbsp;&nbsp;→ (E+M) =10: +5점
                <br />
                <br />
                3) 그 외의 경우:
                <br />
                &nbsp;&nbsp;&nbsp;→ E + V + M 단순 합산
                <br />
                <br />★ 점수 범위: 0~15점
              </span>
            </span>
          </label>
          <div className="input_2col">
            <input
              type="text"
              name={`${prefix}gcs_best_total`}
              value={formData[`${prefix}gcs_best_total`] || ""}
              readOnly
              style={{
                backgroundColor: "#f5f5f5",
                cursor: "not-allowed",
                fontWeight: "bold",
                color: "#007bff",
              }}
            />
          </div>
        </div>
      </div>

      {/* Best Pupil Assessment */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Best pupil_symmetry ({prefix}pupil_best_sym)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {["Equal", "Unequal R>L", "Unequal L>R"].map((opt) => (
                <label key={opt} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}pupil_best_sym`}
                    value={opt}
                    checked={formData[`${prefix}pupil_best_sym`] === opt}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="block-2column" />
      </div>

      {/* Best Right Pupil Assessment */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Best pupil_reflex_right ({prefix}pupil_best_lr_rt)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "+ (Brisk)", value: "(Brisk) +" },
                { label: "+ (Sluggish)", value: "(Sluggish) +" },
                { label: "- (Negative)", value: "(Negative) -" },
              ].map((option) => (
                <label key={option.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}pupil_best_lr_rt`}
                    value={option.value}
                    checked={
                      formData[`${prefix}pupil_best_lr_rt`] === option.value
                    }
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{option.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Best pupil_size_right ({prefix}pupil_best_size_rt)
          </label>
          <div className="input_2col">
            <input
              type="text"
              name={`${prefix}pupil_best_size_rt`}
              value={formData[`${prefix}pupil_best_size_rt`] || ""}
              onChange={handleChange}
            />
            <span className="unit-text">mm</span>
          </div>
        </div>
      </div>

      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Best pupil_reflex_unknown_right ({prefix}pupil_best_unknown_rt)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {["Untestable", "Unknown"].map((opt) => (
                <label key={opt} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}pupil_best_unknown_rt`}
                    value={opt}
                    checked={formData[`${prefix}pupil_best_unknown_rt`] === opt}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="block-2column" />
      </div>

      {/* Best Left Pupil Assessment */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Best pupil_reflex_left ({prefix}pupil_best_lr_lt)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "+ (Brisk)", value: "(Brisk) +" },
                { label: "+ (Sluggish)", value: "(Sluggish) +" },
                { label: "- (Negative)", value: "(Negative) -" },
              ].map((option) => (
                <label key={option.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}pupil_best_lr_lt`}
                    value={option.value}
                    checked={
                      formData[`${prefix}pupil_best_lr_lt`] === option.value
                    }
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{option.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Best pupil_size_left ({prefix}pupil_best_size_lt)
          </label>
          <div className="input_2col">
            <input
              type="text"
              name={`${prefix}pupil_best_size_lt`}
              value={formData[`${prefix}pupil_best_size_lt`] || ""}
              onChange={handleChange}
            />
            <span className="unit-text">mm</span>
          </div>
        </div>
      </div>

      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Best pupil_reflex_unknown_left ({prefix}pupil_best_unknown_lt)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {["Untestable", "Unknown"].map((opt) => (
                <label key={opt} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}pupil_best_unknown_lt`}
                    value={opt}
                    checked={formData[`${prefix}pupil_best_unknown_lt`] === opt}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="block-2column" />
      </div>

      {/* Neurological Worsening Section */}
      <div className="basic-info-fieldset">
        <h3 className="section-subtitle"></h3>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Neurological worsening 여부 ({prefix}neuroworse)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {["No", "Yes"].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name={`${prefix}neuroworse`}
                      value={opt}
                      checked={formData[`${prefix}neuroworse`] === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Neurological worsening 의 종류 ({prefix}neuroworse_type)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "GCS total 2점 이상 악화될 경우",
                  "Development of pupillary abnormalities",
                  "Other neurological and/or CT deterioration",
                ].map((opt) => (
                  <label key={opt} className="checkbox-label">
                    <input
                      type="checkbox"
                      name={`${prefix}neuroworse_type`}
                      value={opt}
                      checked={formData[`${prefix}neuroworse_type`]?.includes(
                        opt
                      )}
                      onChange={handleCheckboxChange}
                    />

                    <div className="chk-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Neurological worsening 의 대처 ({prefix}neuroworse_action)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "None",
                  "Unscheduled CT scan",
                  "Change in medical therapy",
                  "Surgical intervention",
                ].map((opt) => (
                  <label key={opt} className="checkbox-label">
                    <input
                      type="checkbox"
                      name={`${prefix}neuroworse_action`}
                      value={opt}
                      checked={formData[`${prefix}neuroworse_action`]?.includes(
                        opt
                      )}
                      onChange={handleCheckboxChange}
                    />
                    <div className="chk-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column" />
        </div>
      </div>

      {/* TIL (Therapeutic Intensity Level) Section */}
      <div className="basic-info-fieldset">
        <h3 className="section-subtitle"></h3>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Therapeutic Intensity Level (TIL) 체크 일시 ({prefix}til_dt)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name={`${prefix}til_dt`}
                placeholder="YYYY-MM-DD HH:MM"
                value={dtDisplay(formData[`${prefix}til_dt`])}
                onChange={(e) => {
                  const input = e.target.value;
                  const currentValue =
                    dtDisplay(formData[`${prefix}til_dt`]) || "";

                  // 현재 입력된 값에서 이전 값보다 짧아졌다면 백스페이스로 간주
                  if (input.length < currentValue.length) {
                    handleChange(e);
                    return;
                  }

                  // 숫자만 추출
                  const numbers = input.replace(/\D/g, "");

                  if (numbers.length <= 12) {
                    // 최대 12자리 (YYYYMMDDHHmm)
                    let formattedValue = numbers;

                    // 날짜 시간 포맷팅
                    if (numbers.length >= 4) {
                      formattedValue =
                        numbers.slice(0, 4) + "-" + numbers.slice(4);
                    }
                    if (numbers.length >= 6) {
                      formattedValue =
                        formattedValue.slice(0, 7) +
                        "-" +
                        formattedValue.slice(7);
                    }
                    if (numbers.length >= 8) {
                      formattedValue =
                        formattedValue.slice(0, 10) +
                        " " +
                        formattedValue.slice(10);
                    }
                    if (numbers.length >= 10) {
                      formattedValue =
                        formattedValue.slice(0, 13) +
                        ":" +
                        formattedValue.slice(13);
                    }

                    // handleChange 함수 호출
                    handleChange({
                      target: {
                        name: `${prefix}til_dt`,
                        value: formattedValue,
                      },
                    });
                  }
                }}
                onPaste={(e) => handlePaste(e, `${prefix}til_dt`)}
              />
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Form Completion Status ({prefix}til_complete)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  "Not Started",
                  "In Process",
                  "Complete",
                  "Queries Outstanding",
                  "Not Performed",
                ].map((opt) => (
                  <label key={opt} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name={`${prefix}til_complete`}
                      value={opt}
                      checked={formData[`${prefix}til_complete`] === opt}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* TIL Position Management */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Head elevation for ICP control ({prefix}til_position)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name={`${prefix}til_position`}
                      value={opt.value}
                      checked={formData[`${prefix}til_position`] === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Nursed flat (180°) for CPP management ({prefix}til_position_flat)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name={`${prefix}til_position_flat`}
                      value={opt.value}
                      checked={
                        formData[`${prefix}til_position_flat`] === opt.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* TIL Sedation Management */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Sedation (low dose as required for mechanical ventilation) (
              {prefix}til_sedation)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name={`${prefix}til_sedation`}
                      value={opt.value}
                      checked={formData[`${prefix}til_sedation`] === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Higher dose sedation for ICP control (not aiming for burst
              supression) ({prefix}til_sedation_higher)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name={`${prefix}til_sedation_higher`}
                      value={opt.value}
                      checked={
                        formData[`${prefix}til_sedation_higher`] === opt.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Metabolic suppression for ICP control with high dose barbiturates
              or propofol ({prefix}til_sedation_suppression)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name={`${prefix}til_sedation_suppression`}
                      value={opt.value}
                      checked={
                        formData[`${prefix}til_sedation_suppression`] ===
                        opt.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Neuromuscular blockade (paralysis) ({prefix}til_sedation_nmb)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name={`${prefix}til_sedation_nmb`}
                      value={opt.value}
                      checked={
                        formData[`${prefix}til_sedation_nmb`] === opt.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CSF Drainage */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              CSF Drainage ({prefix}til_csfdrain)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name={`${prefix}til_csfdrain`}
                      value={opt.value}
                      checked={formData[`${prefix}til_csfdrain`] === opt.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Volume drained (ml) ({prefix}til_csfdrainvol)
            </label>
            <div className="input_2col">
              <input
                type="text"
                name={`${prefix}til_csfdrainvol`}
                value={formData[`${prefix}til_csfdrainvol`] || ""}
                onChange={handleChange}
              />
              <span className="unit-text">ml/day</span>
            </div>
          </div>
        </div>

        {/* Fluid Management */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Fluid loading for maintenance of cerebral perfusion ({prefix}
              til_fluidloading)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name={`${prefix}til_fluidloading`}
                      value={opt.value}
                      checked={
                        formData[`${prefix}til_fluidloading`] === opt.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Vasopressor therapy required for management of cerebral perfusion
              ({prefix}til_vasopressor)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name={`${prefix}til_vasopressor`}
                      value={opt.value}
                      checked={
                        formData[`${prefix}til_vasopressor`] === opt.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Ventilation Management */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Mild hypocapnia for ICP control [PaCO2 ≤35 and {"<"} 40] ({prefix}
              til_hyperventilation)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name={`${prefix}til_hyperventilation`}
                      value={opt.value}
                      checked={
                        formData[`${prefix}til_hyperventilation`] === opt.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Moderate hypocapnia for ICP control [PaCO2 ≤30 and {"<"} 35] (
              {prefix}til_hyperventilation_mod)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name={`${prefix}til_hyperventilation_mod`}
                      value={opt.value}
                      checked={
                        formData[`${prefix}til_hyperventilation_mod`] ===
                        opt.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Intensive hypocapnia for ICP control [PaCO2 ({"<"} 30 mmHg)] (
              {prefix}til_hyperventilation_intense)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name={`${prefix}til_hyperventilation_intense`}
                      value={opt.value}
                      checked={
                        formData[`${prefix}til_hyperventilation_intense`] ===
                        opt.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Osmotic Therapy */}
        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Hyperosmolar therapy with mannitol up to 2 g/kg/24 hours ({prefix}
              til_osm_mnt_low)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name={`${prefix}til_osm_mnt_low`}
                      value={opt.value}
                      checked={
                        formData[`${prefix}til_osm_mnt_low`] === opt.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="block-2column">
            <label className="item_title_2col">
              Hyperosmolar therapy with hypertonic saline up to 0.3 g/kg/24
              hours ({prefix}til_osm_hts_low)
            </label>
            <div className="input_2col">
              <div className="rad-group vertical">
                {[
                  { label: "No", value: "0 : No" },
                  { label: "Yes", value: "1 : Yes" },
                ].map((opt) => (
                  <label key={opt.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name={`${prefix}til_osm_hts_low`}
                      value={opt.value}
                      checked={
                        formData[`${prefix}til_osm_hts_low`] === opt.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{opt.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* High Dose Osmolar Therapy */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Hyperosmolar therapy with mannitol {">"} 2 g/kg/24 hours ({prefix}
            til_osm_mnt_high)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}til_osm_mnt_high`}
                    value={opt.value}
                    checked={
                      formData[`${prefix}til_osm_mnt_high`] === opt.value
                    }
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Hyperosmolar therapy with hypertonic saline {">"} 0.3 g/kg/24 hours
            ({prefix}til_osm_hts_high)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}til_osm_hts_high`}
                    value={opt.value}
                    checked={
                      formData[`${prefix}til_osm_hts_high`] === opt.value
                    }
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Temperature Management */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Treatment of fever (temperature {">"} 38℃) or spontaneous
            hypothermia (temperature {"<"} 34.5℃) ({prefix}til_fever)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}til_fever`}
                    value={opt.value}
                    checked={formData[`${prefix}til_fever`] === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Mild hypothermia for ICP control with a lower limit of 35°C (
            {prefix}til_ttm_mild)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}til_ttm_mild`}
                    value={opt.value}
                    checked={formData[`${prefix}til_ttm_mild`] === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Hypothermia below 35°C ({prefix}til_ttm_moderate)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}til_ttm_moderate`}
                    value={opt.value}
                    checked={
                      formData[`${prefix}til_ttm_moderate`] === opt.value
                    }
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Surgery Management */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Intracranial operation for progressive mass lesion, not scheduled on
            admission ({prefix}til_icp_surgery)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}til_icp_surgery`}
                    value={opt.value}
                    checked={formData[`${prefix}til_icp_surgery`] === opt.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="block-2column">
          <label className="item_title_2col">
            Decompressive craniectomy ({prefix}til_icp_decomcrani)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No", value: "0 : No" },
                { label: "Yes", value: "1 : Yes" },
              ].map((opt) => (
                <label key={opt.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}til_icp_decomcrani`}
                    value={opt.value}
                    checked={
                      formData[`${prefix}til_icp_decomcrani`] === opt.value
                    }
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{opt.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Change Reason */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Reason for change in treatment over this day ({prefix}
            til_change_reason)
          </label>
          <div className="input_2col">
            <div className="rad-group vertical">
              {[
                { label: "No change", value: "No change" },
                {
                  label: "Intensified: Clinical deterioration",
                  value: "Intensified: Clinical deterioration",
                },
                {
                  label:
                    "Intensified:Suspicion of increased of ICP (not measured)",
                  value:
                    "Intensified:Suspicion of increased of ICP (not measured)",
                },
                {
                  label: "Intensified:Increased ICP (documented)",
                  value: "Intensified:Increased ICP (documented)",
                },
                {
                  label:
                    "Intensified:Clinical decision to target other mechanism",
                  value:
                    "Intensified:Clinical decision to target other mechanism",
                },
                {
                  label: "Intensified:Change of doctor (different shift)",
                  value: "Intensified:Change of doctor (different shift)",
                },
                {
                  label: "Decreasing:Clinical improvement",
                  value: "Decreasing:Clinical improvement",
                },
                {
                  label: "Decreasing:Adequate control over ICP",
                  value: "Decreasing:Adequate control over ICP",
                },
                {
                  label: "Decreasing:Upper treatment limit reached/past",
                  value: "Decreasing:Upper treatment limit reached/past",
                },
                {
                  label: "Decreasing:Further treatment considered futile",
                  value: "Decreasing:Further treatment considered futile",
                },
                {
                  label: "Decreasing:Change of doctor (different shift)",
                  value: "Decreasing:Change of doctor (different shift)",
                },
              ].map((option) => (
                <label key={option.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={`${prefix}til_change_reason`}
                    value={option.value}
                    checked={
                      formData[`${prefix}til_change_reason`] === option.value
                    }
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{option.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* TIL Summary */}
      <div className="input-wrapper-2column">
        <div className="block-2column">
          <label className="item_title_2col">
            Therapeutic Intensity Level 총점 ({prefix}til_summary)
            <span className="tooltip-container">
              <span className="tooltip-icon">?</span>
              <span className="tooltip-text">
                【TIL Score 자동계산 공식】
                <br />
                <br />
                1. Position Management (최대값 적용)
                <br />• 두부 거상: 1점
                <br />• 평면 자세: 1점
                <br />
                <br />
                2. Sedation Management (최대값 적용)
                <br />• 기본 진정: 1점
                <br />• 고용량 진정: 2점
                <br />• 발작 억제: 5점
                <br />
                <br />
                3. Neuromuscular Blockade
                <br />• 근이완제: 3점
                <br />
                <br />
                4. CSF Drainage
                <br />• {"<"}120ml/day: 2점
                <br />• ≥120ml/day: 3점
                <br />
                <br />
                5. Fluid/BP Management (합산)
                <br />• 수액 부하: 1점
                <br />• 승압제: 1점
                <br />
                <br />
                6. Ventilation (최대값 적용)
                <br />• 경도 과환기: 1점
                <br />• 중등도 과환기: 2점
                <br />• 심한 과환기: 4점
                <br />
                <br />
                7. Osmolar Therapy (각각 최대값 적용)
                <br />• Mannitol 저용량: 2점
                <br />• Mannitol 고용량: 3점
                <br />• HTS 저용량: 2점
                <br />• HTS 고용량: 3점
                <br />
                <br />
                8. Temperature Management (최대값 적용)
                <br />• 발열 관리: 1점
                <br />• 경도 저체온: 2점
                <br />• 중등도 저체온: 5점
                <br />
                <br />
                9. Surgery (합산)
                <br />• 두개내 수술: 4점
                <br />• 감압 두개절제술: 5점
              </span>
            </span>
          </label>
          <div className="input_2col">
            <input
              type="text"
              name={`${prefix}til_summary`}
              value={formData[`${prefix}til_summary`] || ""}
              readOnly
              style={{
                backgroundColor: "#f5f5f5",
                cursor: "not-allowed",
                fontWeight: "bold",
                color: "#007bff",
              }}
            />
          </div>
        </div>
        <div className="block-2column" />
      </div>
    </div>
  );
};

export default DailySection;
